import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { MailCheck } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { authErrorText, authRedirect, supabase, supabaseConfigured } from "@/lib/supabase";
import { LogoMark } from "@/components/Shell";
import { cn } from "@/lib/utils";

const SIDE = "/images/landing/golden-hub-technology-cube.png";

function GoogleButton({ onError }) {
  const go = async () => {
    const { error } = await supabase.auth.signInWithOAuth({ provider: "google", options: { redirectTo: authRedirect("/auth/callback") } });
    if (error) onError(authErrorText(error));
  };
  return (
    <button type="button" onClick={go} data-testid="google-signin-button" className="w-full btn-outline h-12 justify-center">
      <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true"><path fill="#EA4335" d="M24 9.5c3.5 0 6.6 1.2 9 3.5l6.7-6.7C35.6 2.6 30.2 0 24 0 14.6 0 6.5 5.4 2.6 13.2l7.8 6C12.3 13.4 17.7 9.5 24 9.5z"/><path fill="#4285F4" d="M46.5 24.5c0-1.6-.1-2.8-.4-4H24v7.6h12.7c-.3 2.1-1.7 5.3-4.8 7.4l7.4 5.7c4.4-4.1 7.2-10 7.2-16.7z"/><path fill="#FBBC05" d="M10.4 28.8c-.5-1.5-.8-3.1-.8-4.8s.3-3.3.8-4.8l-7.8-6C1 16.4 0 20.1 0 24s1 7.6 2.6 10.8l7.8-6z"/><path fill="#34A853" d="M24 48c6.2 0 11.5-2.1 15.3-5.7l-7.4-5.7c-2 1.4-4.6 2.3-7.9 2.3-6.3 0-11.7-3.9-13.6-9.3l-7.8 6C6.5 42.6 14.6 48 24 48z"/></svg>
      Continue with Google
    </button>
  );
}

export default function AuthPage() {
  const [mode, setMode] = useState("signup");
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [error, setError] = useState(supabaseConfigured ? "" : "Sign-in isn't configured yet (Supabase keys are missing).");
  const [busy, setBusy] = useState(false);
  const [sentTo, setSentTo] = useState("");
  const [resent, setResent] = useState(false);
  const [existing, setExisting] = useState(false);
  const { refresh, loading, isAuthenticated, org } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  useEffect(() => {
    if (loading || !isAuthenticated) return;
    const from = location.state?.from;
    const destination = typeof from === "string" && /^\/(home|brand|create|library|crm|grow|launch|persona|compose|admin)(\/|$)/.test(from) ? from : "/home";
    navigate(org ? destination : "/onboarding", { replace: true });
  }, [loading, isAuthenticated, org, location.state, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setExisting(false);
    setBusy(true);
    try {
      if (mode === "signup") {
        const { data, error: err } = await supabase.auth.signUp({
          email: form.email, password: form.password,
          options: { data: { full_name: form.name }, emailRedirectTo: authRedirect("/auth/callback") },
        });
        // Supabase doesn't reveal existing emails: it sends nothing and returns a user with no identities.
        if (err?.code === "user_already_exists" || (data?.user && data.user.identities?.length === 0)) return setExisting(true);
        if (err) return setError(authErrorText(err));
        if (!data.session) return setSentTo(form.email);  // confirmation email sent
      } else {
        const { error: err } = await supabase.auth.signInWithPassword({ email: form.email, password: form.password });
        if (err?.code === "email_not_confirmed") return setSentTo(form.email);
        if (err) return setError(authErrorText(err));
      }
      const me = await refresh();
      if (!me) return setError("Signed in, but your account couldn't be loaded. Try again in a moment.");
      navigate(me.org ? "/home" : "/onboarding", { replace: true });
    } finally {
      setBusy(false);
    }
  };

  const resend = async () => {
    const { error: err } = await supabase.auth.resend({ type: "signup", email: sentTo, options: { emailRedirectTo: authRedirect("/auth/callback") } });
    if (err) setError(authErrorText(err)); else setResent(true);
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-background">
      <div className="relative hidden lg:block bg-black on-dark">
        <img src={SIDE} alt="Golden LegacyStack AI technology cube connecting people, marketing, growth, and business systems" className="absolute inset-0 h-full w-full object-cover opacity-70" />
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent" />
        <div className="relative h-full flex flex-col justify-between p-12 text-white">
          <Link to="/" className="flex items-center gap-3"><LogoMark /><span className="font-display text-lg">LegacyStack AI</span></Link>
          <div>
            <p className="eyebrow-label !text-gold mb-4">Your first win</p>
            <h2 className="font-display text-4xl leading-tight max-w-md">A published, image-rich website and nine designed posts — before your coffee cools.</h2>
          </div>
        </div>
      </div>
      <div className="flex items-center justify-center px-5 py-16">
        <div className="w-full max-w-md">
          <Link to="/" className="lg:hidden flex items-center gap-3 mb-10"><LogoMark /><span className="font-display text-lg">LegacyStack AI</span></Link>
          <div className="flex gap-1 p-1 rounded-full border border-border w-fit mb-8" role="tablist">
            {[["signup", "Create account"], ["login", "Sign in"]].map(([m, l]) => (
              <button key={m} type="button" role="tab" aria-selected={mode === m} data-testid={`auth-tab-${m}`} onClick={() => { setMode(m); setError(""); setExisting(false); }} className={cn("px-4 h-9 rounded-full text-sm transition-colors", mode === m ? "bg-foreground text-background" : "text-muted-foreground hover:text-foreground")}>{l}</button>
            ))}
          </div>
          {sentTo ? (
            <div className="space-y-5" data-testid="auth-check-email">
              <MailCheck className="h-8 w-8 text-gold" />
              <h1 className="font-display text-3xl sm:text-4xl">Check your email.</h1>
              <p className="text-sm text-muted-foreground">We sent a confirmation link to <span className="text-foreground">{sentTo}</span>. Open it to finish creating your account — your 100 trial credits arrive once it's confirmed.</p>
              {error && <p className="text-sm text-destructive" role="alert" data-testid="auth-error">{error}</p>}
              <div className="flex flex-wrap gap-3">
                <button type="button" onClick={resend} disabled={resent} className="btn-outline h-11 px-5" data-testid="auth-resend">{resent ? "Sent again" : "Resend the link"}</button>
                <button type="button" onClick={() => { setSentTo(""); setResent(false); setMode("login"); }} className="h-11 px-5 text-sm text-muted-foreground hover:text-foreground" data-testid="auth-back">Back to sign in</button>
              </div>
            </div>
          ) : (<>
          <h1 className="font-display text-3xl sm:text-4xl">{mode === "signup" ? "Begin your legacy." : "Welcome back."}</h1>
          <p className="text-sm text-muted-foreground mt-2">{mode === "signup" ? "New accounts receive 100 trial credits — enough for the full first win." : "Sign in to return to your studio."}</p>
          <div className="mt-8 space-y-4">
            <GoogleButton onError={setError} />
            <div className="flex items-center gap-3 text-xs text-muted-foreground"><span className="h-px flex-1 bg-border" />or with email<span className="h-px flex-1 bg-border" /></div>
            <form onSubmit={submit} className="space-y-3" data-testid="auth-form">
              {mode === "signup" && <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Your name" autoComplete="name" data-testid="auth-name-input" className="w-full h-12 rounded-xl border border-border bg-background px-4 text-sm focus:outline-none focus:ring-2 focus:ring-gold/60" />}
              <input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="Email" autoComplete="email" data-testid="auth-email-input" className="w-full h-12 rounded-xl border border-border bg-background px-4 text-sm focus:outline-none focus:ring-2 focus:ring-gold/60" />
              <input required type="password" minLength={8} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder={mode === "signup" ? "Password (8+ characters)" : "Password"} autoComplete={mode === "signup" ? "new-password" : "current-password"} data-testid="auth-password-input" className="w-full h-12 rounded-xl border border-border bg-background px-4 text-sm focus:outline-none focus:ring-2 focus:ring-gold/60" />
              {error && <p className="text-sm text-destructive" role="alert" data-testid="auth-error">{error}</p>}
              {existing && (
                <div className="rounded-xl border border-border p-4 text-sm space-y-3" role="status" data-testid="auth-existing">
                  <p>This email already has an account — sign in or reset your password.</p>
                  <div className="flex flex-wrap gap-2">
                    <button type="button" className="btn-outline h-9 px-4" data-testid="auth-existing-signin" onClick={() => { setMode("login"); setExisting(false); setForm({ ...form, password: "" }); }}>Sign in</button>
                    <Link to="/auth/forgot" state={{ email: form.email }} className="btn-outline h-9 px-4 inline-flex items-center" data-testid="auth-existing-reset">Reset password</Link>
                  </div>
                </div>
              )}
              <button type="submit" disabled={busy || !supabaseConfigured} className="btn-gold w-full h-12" data-testid="auth-submit-button">{busy ? "One moment…" : mode === "signup" ? "Create account" : "Sign in"}</button>
            </form>
            <p className="text-xs text-muted-foreground">Forgot your password? <Link to="/auth/forgot" className="text-foreground underline underline-offset-4" data-testid="auth-forgot-link">Reset it by email</Link>.</p>
          </div>
          </>)}
        </div>
      </div>
    </div>
  );
}
